import { writable, derived } from 'svelte/store';

// Static App Constants
export const ulterraTeal = "rgb(0, 146, 159)";
export const super3 = "\u00B3";
export const super2 = "\u00B2";
export const deltaP = "\u0394";

// Unit Conversion Factors
export const baseUnit = 1;
export const sizeConversionInchesToMM = 25.4;
export const sizeConversionInches32nd = 32;
export const flowConversionCubicMeters = 0.00378541;
export const flowConversionLitersPerMinute = 3.785412;
export const flowConversionCubicFeetPerMinute = 0.133681;
export const flowConversionBarrelsPerMinute = 0.02381;
export const mudwtConversionKilosPerCubicMeter = 119.826;
export const mudwtConversionkPaPerMeter = 1.175668;
export const mudwtConversionPSIPer1kFt = 51.9719;
export const mudwtConversionPoundsPerCubicFeet = 7.481201;
export const mudwtConversionSG = 0.119826;
export const pressureConversionKpa = 6.89476;
export const pressureConversionBar = 0.068948;
export const pressureConversionAtm = 0.068046;

// User Input Stores
export const nozzle_qty_1 = writable(6);
export const nozzle_diam_1 = writable(14);
export const nozzle_qty_2 = writable(0);
export const nozzle_diam_2 = writable(0);
export const nozzle_qty_3 = writable(0);
export const nozzle_diam_3 = writable(0);
export const flow_rate = writable(450);
export const mud_weight = writable(8.4);
export const bit_size = writable(8.75);

// Unit Conversion Stores
export const bitSizeConversion = writable(baseUnit);
export const bitSizeDisplayUnit = derived([bitSizeConversion],
    ([$bitSizeConversion]) => {
        if ($bitSizeConversion == baseUnit) {
            return "(in)";
        } else {
            return "(mm)";
        }
    });
export const bitSizeInInches = derived(
    [bit_size, bitSizeConversion], ([$bit_size, $bitSizeConversion]) => {
        return $bit_size / $bitSizeConversion;
    });

export const nozzleDiameterConversion = writable(sizeConversionInches32nd);
export const nozzleSizeDisplayUnit = derived(
    nozzleDiameterConversion, ($nozzleDiameterConversion) => {
        if ($nozzleDiameterConversion == sizeConversionInches32nd) {
            return "(32nds)";
        } else {
            return "(mm)";
        }
    });

export const tfaConversion = writable(baseUnit);
export const tfaDisplayUnit = derived(
    tfaConversion, ($tfaConversion) => {
        if ($tfaConversion == baseUnit) {
            return "in";
        } else {
            return "mm";
        }
    });
export const tfaSigFigs = derived(
    tfaConversion, ($tfaConversion) => {
        if ($tfaConversion == baseUnit) {
            return 3
        } else {
            return 0;
        }
    });

export const flowRateConversion = writable(baseUnit);
export const flowRateDisplayUnit = derived(
    flowRateConversion, ($flowRateConversion) => {
        switch ($flowRateConversion) {
            case baseUnit:
                return "gpm";
            case flowConversionCubicMeters:
                return "m" + super3 + "/min";
            case flowConversionLitersPerMinute:
                return "l/min";
            case flowConversionCubicFeetPerMinute:
                return "ft" + super3 + "/min";
            case flowConversionBarrelsPerMinute:
                return "bbl/min";
        }
    });

export const mudWtConversion = writable(baseUnit);
export const mudWtDisplayUnit = derived(
    mudWtConversion, ($mudWtConversion) => {
        switch ($mudWtConversion) {
            case baseUnit:
                return "ppg";
            case mudwtConversionKilosPerCubicMeter:
                return "kg/m" + super3;
            case mudwtConversionkPaPerMeter:
                return "kPa/m";
            case mudwtConversionPSIPer1kFt:
                return "psi/1kft";
            case mudwtConversionPoundsPerCubicFeet:
                return "lb/ft" + super3;
            case mudwtConversionSG:
                return "sg";
        }
    });

export const pressureDropConversion = writable(baseUnit);
export const pressureDropDisplayUnit = derived(
    pressureDropConversion, ($pressureDropConversion) => {
        switch ($pressureDropConversion) {
            case baseUnit:
                return "(psi)";
            case pressureConversionKpa:
                return "(kPa)";
            case pressureConversionBar:
                return "(bar)";
            case pressureConversionAtm:
                return "(atm)";
        }
    }
);

export const jetVelocityConversion = writable(baseUnit);
export const jetVelocityDisplayUnit = derived(
    jetVelocityConversion, ($jetVelocityConversion) => {
        if ($jetVelocityConversion == baseUnit) {
            return "(ft/s)";
        } else {
            return "(m/s)";
        }
    }
);

export const impactForceConversion = writable(baseUnit);
export const jetImpactForceDisplayUnit = derived(
    impactForceConversion, ($impactForceConversion) => {
        if ($impactForceConversion == baseUnit) {
            return "(lbf)";
        } else {
            return "(N)";
        }
    }
);

export const hhpConversion = writable(baseUnit);
export const hhpDisplayUnit = derived(
    hhpConversion, ($hhpConversion) => {
        if ($hhpConversion == baseUnit) {
            return "(Hp)";
        } else {
            return "(kW)";
        }
    }
);

export const hsiConversion = writable(baseUnit);
export const hsiDisplayUnit = derived(
    hsiConversion, ($hsiConversion) => {
        if ($hsiConversion == baseUnit) {
            return "(hsi)";
        } else {
            return "(kW/cm" + super2 + ")";
        }
    }
);

export const nozzleSizeTFACardDisplayUnit = derived(
    nozzleDiameterConversion, ($nozzleDiameterConversion) => {
        if ($nozzleDiameterConversion == sizeConversionInches32nd) {
            return "/32";
        } else {
            return "mm";
        }
    }
);

//  Hydraulics Calculations
function tfa_imperial(nozzleQty, nozzleDiamInInches) {
    const nozzleRadius = nozzleDiamInInches / 2;
    return nozzleQty * Math.PI * Math.pow(nozzleRadius, 2);
}
const nozzle1DiamInInches = derived(
    [nozzle_diam_1, nozzleDiameterConversion], ([$nozzle_diam_1, $nozzleDiameterConversion]) => {
        return $nozzle_diam_1 / $nozzleDiameterConversion;
    });
const nozzle2DiamInInches = derived(
    [nozzle_diam_2, nozzleDiameterConversion], ([$nozzle_diam_2, $nozzleDiameterConversion]) => {
        return $nozzle_diam_2 / $nozzleDiameterConversion;
    });
const nozzle3DiamInInches = derived(
    [nozzle_diam_3, nozzleDiameterConversion], ([$nozzle_diam_3, $nozzleDiameterConversion]) => {
        return $nozzle_diam_3 / $nozzleDiameterConversion;
    });
const tfa_1_in = derived(
    [nozzle_qty_1, nozzle1DiamInInches], ([$nozzle_qty_1, $nozzle1DiamInInches]) => {
        return tfa_imperial($nozzle_qty_1, $nozzle1DiamInInches);
    })
const tfa_2_in = derived(
    [nozzle_qty_2, nozzle2DiamInInches], ([$nozzle_qty_2, $nozzle2DiamInInches]) => {
        return tfa_imperial($nozzle_qty_2, $nozzle2DiamInInches);
    })
const tfa_3_in = derived(
    [nozzle_qty_3, nozzle3DiamInInches], ([$nozzle_qty_3, $nozzle3DiamInInches]) => {
        return tfa_imperial($nozzle_qty_3, $nozzle3DiamInInches);
    })
export const tfa_1 = derived(
    [tfa_1_in, tfaConversion], ([$tfa_1_in, $tfaConversion]) => {
        return $tfa_1_in * $tfaConversion;
    }
)
export const tfa_2 = derived(
    [tfa_2_in, tfaConversion], ([$tfa_2_in, $tfaConversion]) => {
        return $tfa_2_in * $tfaConversion;
    }
)
export const tfa_3 = derived(
    [tfa_3_in, tfaConversion], ([$tfa_3_in, $tfaConversion]) => {
        return $tfa_3_in * $tfaConversion;
    }
)
export const tfa_total_in_sq_in = derived(
    [tfa_1_in, tfa_2_in, tfa_3_in], ([$tfa_1_in, $tfa_2_in, $tfa_3_in]) => {
        return $tfa_1_in + $tfa_2_in + $tfa_3_in;
    }
)
export const tfa_total = derived(
    [tfa_1_in, tfa_2_in, tfa_3_in, tfaConversion], ([$tfa_1_in, $tfa_2_in, $tfa_3_in, $tfaConversion]) => {
        return ($tfa_1_in + $tfa_2_in + $tfa_3_in) * $tfaConversion;
    }
)

// Imperial Unit Staging
export const mudWtInPpg = derived(
    [mud_weight, mudWtConversion], ([$mud_weight, $mudWtConversion]) => {
        return $mud_weight / $mudWtConversion;
    });
export const flowRateInGpm = derived(
    [flow_rate, flowRateConversion], ([$flow_rate, $flowRateConversion]) => {
        return $flow_rate / $flowRateConversion;
    });
const jet_velocity_in_ft_per_sec = derived(
    [flowRateInGpm, tfa_total_in_sq_in], ([$flowRateInGpm, $tfa_total_in_sq_in]) => {
        return 0.321 * ($flowRateInGpm / $tfa_total_in_sq_in);
    });
const impact_force_in_ft_lb = derived(
    [flowRateInGpm, mudWtInPpg, jet_velocity_in_ft_per_sec],
    ([$flowRateInGpm, $mudWtInPpg, $jet_velocity_in_ft_per_sec], ) => {
        return ($flowRateInGpm * $mudWtInPpg * $jet_velocity_in_ft_per_sec) / 1930;
    });

const pressure_drop_at_bit_in_psi = derived(
    [mudWtInPpg, flowRateInGpm, tfa_total_in_sq_in],
    ([$mudWtInPpg, $flowRateInGpm, $tfa_total_in_sq_in]) => {
        return ($mudWtInPpg * Math.pow($flowRateInGpm, 2)) /
            (10858 * Math.pow($tfa_total_in_sq_in, 2));
    });
const hhp_in_hp = derived(
    [pressure_drop_at_bit_in_psi, flowRateInGpm],
    ([$pressure_drop_at_bit_in_psi, $flowRateInGpm]) => {
        return ($pressure_drop_at_bit_in_psi * $flowRateInGpm) / 1714;
    });
const hsi_in_psi = derived(
    [hhp_in_hp, bitSizeInInches], ([$hhp_in_hp, $bitSizeInInches]) => {
        return $hhp_in_hp / (Math.PI * Math.pow($bitSizeInInches / 2, 2));
    });

// Final Unit Outputs
export const pressure_drop_at_bit = derived(
    [pressure_drop_at_bit_in_psi, pressureDropConversion],
    ([$pressure_drop_at_bit_in_psi, $pressureDropConversion]) => {
        return $pressure_drop_at_bit_in_psi * $pressureDropConversion;
    });
export const jet_velocity = derived(
    [jet_velocity_in_ft_per_sec, jetVelocityConversion],
    ([$jet_velocity_in_ft_per_sec, $jetVelocityConversion]) => {
        return $jet_velocity_in_ft_per_sec * $jetVelocityConversion;
    });
export const jet_impact_force = derived(
    [impact_force_in_ft_lb, impactForceConversion],
    ([$impact_force_in_ft_lb, $impactForceConversion]) => {
        return $impact_force_in_ft_lb * $impactForceConversion;
    });
export const hhp = derived(
    [hhp_in_hp, hhpConversion], ([$hhp_in_hp, $hhpConversion]) => {
        return $hhp_in_hp * $hhpConversion;
    });
export const hsi = derived(
    [hsi_in_psi, hsiConversion], ([$hsi_in_psi, $hsiConversion]) => {
        return $hsi_in_psi * $hsiConversion;
    });

export const tfaModalActiveSet = writable(0);
export const tfaModalQty = writable(0);
export const tfaModalSize = writable(0);
export let open = writable(false);
export function toggleTFAModal() {
    $open = !$open;
}

export function range(start, stop, step) {
    /* This function replicates the range function in python. */
    if (typeof stop == "undefined") {
        // one param defined
        stop = start;
        start = 0;
    }

    if (typeof step == "undefined") {
        step = 1;
    }

    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }

    var result = [];
    for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }

    return result;
}